/**
 * TakeQuizPage — the actual student "Start Exam / Take Quiz" experience.
 *
 * Route: /school/:tenantId/quiz/:quizId/take
 *
 * Loads a quiz from /student/quizzes (so we already get correct-answer
 * stripping and the computed scheduling state), guards against
 * upcoming/closed windows, and submits via /school/tenants/{tid}/quiz-attempts.
 *
 * Designed mobile-first: large tap targets, single question per scroll group,
 * sticky bottom submit on phones.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, ArrowLeft, Clock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;

const getEffectiveToken = () => {
  try {
    const t = sessionStorage.getItem('school_preview_token');
    if (t) return t;
  } catch (_e) { /* sessionStorage unavailable */ }
  return getToken();
};

const portalApi = async (path, opts = {}) => {
  const res = await fetch(`${API}/api/school/portal${path}`, {
    ...opts,
    headers: {
      'Authorization': `Bearer ${getEffectiveToken()}`,
      'Content-Type': 'application/json',
      ...(opts.headers || {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.detail || 'Request failed');
  return body;
};

const tenantApi = async (path, opts = {}) => {
  const res = await fetch(`${API}/api/school${path}`, {
    ...opts,
    headers: {
      'Authorization': `Bearer ${getEffectiveToken()}`,
      'Content-Type': 'application/json',
      ...(opts.headers || {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.detail || 'Request failed');
  return body;
};

export default function TakeQuizPage() {
  const { tenantId, quizId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [quiz, setQuiz] = useState(null);
  const [me, setMe] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [quizzes, meRes] = await Promise.all([
          portalApi('/student/quizzes'),
          portalApi('/me'),
        ]);
        if (cancelled) return;
        const q = (quizzes || []).find((x) => x.id === quizId);
        if (!q) throw new Error('Quiz not found or not assigned to your class');
        setQuiz(q);
        setMe(meRes);
      } catch (e) {
        toast.error(e.message || 'Failed to load quiz');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [quizId]);

  // Countdown when the quiz has a time_limit_minutes; we use the smaller of
  // (time_limit_minutes) and (seconds_until_close) so an early-start near the
  // close time still respects the window.
  useEffect(() => {
    if (!quiz || result) return;
    const limitSec = (quiz.time_limit_minutes || 0) * 60;
    const winSec = quiz.seconds_until_close || 0;
    const start = Math.min(...[limitSec, winSec].filter((v) => v > 0)) || limitSec || winSec;
    if (!start) return;
    setSecondsLeft(start);
    const id = setInterval(() => {
      setSecondsLeft((s) => {
        if (s == null) return s;
        if (s <= 1) { clearInterval(id); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [quiz, result]);

  // Auto-submit when timer reaches zero so the student doesn't lose work.
  useEffect(() => {
    if (secondsLeft === 0 && quiz && !result && !submitting) {
      handleSubmit(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft]);

  const totalQuestions = quiz?.questions?.length || 0;
  const answeredCount = useMemo(() => Object.values(answers).filter((v) => v != null && v !== '').length, [answers]);

  const handleSubmit = async (auto = false) => {
    if (submitting || result) return;
    setSubmitting(true);
    try {
      const payload = {
        quiz_id: quizId,
        student_id: me?.linked_id || me?.id || '',
        answers: (quiz.questions || []).map((_, i) => ({
          question_index: i,
          answer: answers[i] ?? '',
        })),
      };
      const r = await tenantApi(`/tenants/${tenantId}/quiz-attempts`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setResult(r);
      toast.success(auto ? 'Time up — auto-submitted' : 'Submitted');
    } catch (e) {
      toast.error(e.message || 'Submit failed');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        Quiz not found.
      </div>
    );
  }

  // Window guards driven by backend `state` field
  if (quiz.state === 'upcoming') {
    return (
      <GuardScreen
        icon={<Clock className="h-8 w-8 text-amber-500" />}
        title="This quiz hasn't opened yet"
        subtitle={`Opens ${quiz.opens_at ? new Date(quiz.opens_at).toLocaleString() : 'later'}`}
        onBack={() => navigate(-1)}
      />
    );
  }
  if (quiz.state === 'closed') {
    return (
      <GuardScreen
        icon={<AlertTriangle className="h-8 w-8 text-rose-500" />}
        title="This quiz is closed"
        subtitle={quiz.closes_at ? `Closed on ${new Date(quiz.closes_at).toLocaleString()}` : ''}
        onBack={() => navigate(-1)}
      />
    );
  }
  if (!quiz.can_attempt && (quiz.attempt_count || 0) > 0 && !result) {
    return (
      <GuardScreen
        icon={<CheckCircle2 className="h-8 w-8 text-emerald-500" />}
        title="Maximum attempts reached"
        subtitle={`You attempted this ${quiz.attempt_count} time${quiz.attempt_count > 1 ? 's' : ''} · best score ${quiz.best_score ?? 0}%`}
        onBack={() => navigate(-1)}
      />
    );
  }
  if (totalQuestions === 0) {
    return (
      <GuardScreen
        icon={<AlertTriangle className="h-8 w-8 text-amber-500" />}
        title="No questions linked to this quiz"
        subtitle="Ask your teacher to add questions before you can attempt this."
        onBack={() => navigate(-1)}
      />
    );
  }

  // Result screen
  if (result) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-8" data-testid="quiz-result">
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {result.passed ? <CheckCircle2 className="h-6 w-6 text-emerald-500" />
                             : <AlertTriangle className="h-6 w-6 text-amber-500" />}
              {result.passed ? 'You passed!' : 'Submitted'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{result.score_percent ?? result.score_percentage ?? 0}%</p>
            <p className="text-sm text-slate-500 mt-1">
              {result.earned_points} / {result.total_points} points
              {result.attempt_number ? ` · attempt #${result.attempt_number}` : ''}
            </p>
            <Button onClick={() => navigate(-1)} className="mt-6" data-testid="quiz-result-back">
              Back to quizzes
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="take-quiz">
      {/* Sticky header with timer */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="quiz-back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm sm:text-base truncate">{quiz.title}</p>
            <p className="text-xs text-slate-500 truncate">
              {quiz.subject_name} · {totalQuestions} question{totalQuestions !== 1 ? 's' : ''} · {answeredCount} answered
            </p>
          </div>
          {secondsLeft != null && (
            <Badge className={`text-sm tabular-nums ${secondsLeft < 60 ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-700'}`}
                   data-testid="quiz-timer">
              <Clock className="h-3 w-3 mr-1" />{Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
            </Badge>
          )}
        </div>
      </div>

      {/* Questions */}
      <div className="max-w-3xl mx-auto px-4 py-4 space-y-4">
        {quiz.instructions && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="py-3 text-sm">{quiz.instructions}</CardContent>
          </Card>
        )}
        {(quiz.questions || []).map((q, i) => (
          <Card key={i} data-testid={`quiz-q-${i}`}>
            <CardContent className="p-4 sm:p-5 space-y-3">
              <div className="text-sm sm:text-base font-medium leading-snug">
                <span className="text-slate-400 mr-2">{i + 1}.</span>
                {q.question}
              </div>
              {q.type === 'mcq' && (
                <div className="space-y-2">
                  {(q.options || []).map((opt, oi) => (
                    <label key={oi} className="flex items-start gap-3 p-3 rounded border bg-white hover:bg-slate-50 cursor-pointer text-sm min-h-[44px]">
                      <input
                        type="radio"
                        name={`q-${i}`}
                        className="mt-1"
                        checked={answers[i] === opt}
                        onChange={() => setAnswers({ ...answers, [i]: opt })}
                        data-testid={`quiz-q-${i}-opt-${oi}`}
                      />
                      <span className="flex-1">{opt}</span>
                    </label>
                  ))}
                </div>
              )}
              {q.type === 'true_false' && (
                <div className="grid grid-cols-2 gap-2">
                  {['True', 'False'].map((opt) => (
                    <Button
                      key={opt}
                      type="button"
                      variant={answers[i] === opt ? 'default' : 'outline'}
                      onClick={() => setAnswers({ ...answers, [i]: opt })}
                      className="h-12"
                      data-testid={`quiz-q-${i}-${opt.toLowerCase()}`}
                    >
                      {opt}
                    </Button>
                  ))}
                </div>
              )}
              {(q.type === 'short_answer' || q.type === 'text' || !q.type) && (
                <Textarea
                  rows={3}
                  inputMode="text"
                  value={answers[i] || ''}
                  onChange={(e) => setAnswers({ ...answers, [i]: e.target.value })}
                  placeholder="Your answer…"
                  data-testid={`quiz-q-${i}-text`}
                />
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Sticky bottom submit bar (mobile-friendly) */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t shadow-md z-20">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <span className="text-xs text-slate-500 hidden sm:block">
            {answeredCount} / {totalQuestions} answered
          </span>
          <Button
            className="flex-1 h-12 text-base"
            onClick={() => handleSubmit(false)}
            disabled={submitting}
            data-testid="quiz-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Submit
          </Button>
        </div>
      </div>
    </div>
  );
}

const GuardScreen = ({ icon, title, subtitle, onBack }) => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
    <Card className="max-w-md w-full" data-testid="quiz-guard">
      <CardContent className="py-8 text-center space-y-3">
        <div className="mx-auto">{icon}</div>
        <h2 className="text-lg font-semibold">{title}</h2>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
        <Button onClick={onBack} variant="outline" className="mt-2" data-testid="quiz-guard-back">
          Back
        </Button>
      </CardContent>
    </Card>
  </div>
);
